import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  getStatusIcon,
  computeBreakdown,
} from "../../utils/brandAmbassadorUtils";
import { ImgTopBtn } from "../../components/buttons";
import { EarnedIcon } from "../../components/icons";
import { apiFetchAcctPayTransactions } from "../../api/backend/auth";
import "./accountsReceivables.scss";

const PAGE_SIZE = 6;

const AccountsReceivables = () => {
  const { userId } = useParams();
  const { state } = useLocation();
  const rolePerformed = state?.role;

  const [transactions, setTransactions] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [openBreakdowns, setOpenBreakdowns] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(null);
  const breakdownRefs = useRef({});

  const formatAmount = (amt) => `$${Number(amt).toFixed(2)}`;
  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

  const loadPage = async (nextOffset) => {
    try {
      const res = await apiFetchAcctPayTransactions(
        nextOffset,
        PAGE_SIZE,
        userId,
        rolePerformed
      );
      const payload = res.data;
      const page = payload.data || [];
      setTransactions((prev) =>
        nextOffset === 0 ? page : [...prev, ...page]
      );
      setHasMore(payload.meta?.hasMore ?? false);
      setOffset(nextOffset + page.length);
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to load transactions");
    }
  };

  // --- group by week ---
  const weeks = React.useMemo(() => {
    const out = {};
    for (const tx of transactions) {
      const d = new Date(tx.date);
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay()); // Sunday start
      const weekKey = weekStart.toISOString().slice(0, 10); // yyyy-mm-dd
      if (!out[weekKey]) out[weekKey] = [];
      out[weekKey].push(tx);
    }
    return out;
  }, [transactions]);

  useEffect(() => {
    if (transactions.length && !selectedWeek) {
      const allWeeks = Object.keys(weeks).sort().reverse();
      setSelectedWeek(allWeeks[0]);
    }
  }, [transactions, weeks, selectedWeek]);

  // --- filter current week ---
  const currentWeekTxs = weeks[selectedWeek] || [];
  const pendingTxs = currentWeekTxs.filter((tx) => tx.status === "Pending");
  const paidTxs = currentWeekTxs.filter((tx) => tx.status === "Paid");

  const weekTotal = (list) =>
    list.reduce((sum, tx) => sum + Number(tx.totalAmount || 0), 0);

  // --- lifecycle load ---
  useEffect(() => {
    if (!rolePerformed) {
      setError("Missing role");
      setLoading(false);
      return;
    }
    (async () => {
      await loadPage(0);
      setLoading(false);
    })();
  }, [userId, rolePerformed]);

  const handleViewMore = async () => {
    setLoadingMore(true);
    await loadPage(offset);
    setLoadingMore(false);
  };

  const toggleBreakdown = (txId) => {
    setOpenBreakdowns((prev) => {
      const isOpen = !!prev[txId];
      const next = { ...prev, [txId]: !isOpen };
      if (!isOpen) {
        setTimeout(() => {
          breakdownRefs.current[txId]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 0);
      }
      return next;
    });
  };

  if (loading) return <p>Loading accounts receivables…</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div id="acctReceivablesTop" className="acct-receivables-container">
      <h2>Accounts Payable</h2>

      {/* Week selector */}
      <div className="week-filter">
        <label>Select Week: </label>
        <select
          value={selectedWeek || ""}
          onChange={(e) => setSelectedWeek(e.target.value)}
        >
          {Object.keys(weeks)
            .sort()
            .reverse()
            .map((w) => (
              <option key={w} value={w}>
                Week of {new Date(w).toLocaleDateString()}
              </option>
            ))}
        </select>
      </div>

      {/* Two-column layout */}
      <div className="week-columns">
        <div className="column">
          <h3>Pending</h3>
          <p>Total: {formatAmount(weekTotal(pendingTxs))}</p>
          {pendingTxs.map((tx) => {
            const isOpen = !!openBreakdowns[tx.id];
            const breakdown = computeBreakdown(tx.description);
            const dateLabel = formatDate(tx.date);
            return (
              <div key={tx.id} className="transaction-item">
                <div className="transaction-date">{dateLabel}</div>
                <div className="transaction-details">
                  <p>{tx.description}</p>
                  <p>Status: {getStatusIcon(tx.status)} {tx.status}</p>
                  {isOpen && breakdown.length > 0 && (
                    <ul className="breakdown-list">
                      {breakdown.map(({ label, amount }, i) => (
                        <li key={i}>
                          {label}: {formatAmount(amount)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="transaction-amount">
                  {formatAmount(tx.totalAmount)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="column">
          <h3>Paid</h3>
          <p>Total: {formatAmount(weekTotal(paidTxs))}</p>
          {paidTxs.map((tx) => {
            const isOpen = !!openBreakdowns[tx.id];
            const breakdown = computeBreakdown(tx.description);
            const dateLabel = formatDate(tx.date);
            return (
              <div key={tx.id} className="transaction-item">
                <div className="transaction-date">{dateLabel}</div>
                <div className="transaction-details">
                  <p>{tx.description}</p>
                  <p>Status: {getStatusIcon(tx.status)} {tx.status}</p>
                  {isOpen && breakdown.length > 0 && (
                    <ul className="breakdown-list">
                      {breakdown.map(({ label, amount }, i) => (
                        <li key={i}>
                          {label}: {formatAmount(amount)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="transaction-amount">
                  {formatAmount(tx.totalAmount)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {hasMore && (
        <button
          className="view-more-btn"
          onClick={handleViewMore}
          disabled={loadingMore}
        >
          {loadingMore ? "Loading…" : "View More"}
        </button>
      )}
    </div>
  );
};

export default AccountsReceivables;



// 1. Why does the dropdown only show this week?

// Right now, the dropdown is built from the weeks actually present in your transactions array:

// {Object.keys(weeks)
//   .sort()
//   .reverse()
//   .map((w) => (
//     <option key={w} value={w}>
//       Week of {new Date(w).toLocaleDateString()}
//     </option>
// ))}


// That means:

// If your DB only has transactions from this week (8/10/2025 → 8/16/2025), then only that week will appear.

// As soon as transactions exist for a different week (say 8/3/2025 or 7/27/2025), those will automatically show up as more <option>s in the dropdown.

// 👉 So yes, once you simulate / seed additional weeks of transactions, the dropdown will expand.

// 2. Why don’t you see the “View More” button?

// This button is controlled by the backend response:

// setHasMore(payload.meta?.hasMore ?? false);


// and in the JSX:

// {hasMore && (
//   <button onClick={handleViewMore} disabled={loadingMore}>
//     {loadingMore ? "Loading…" : "View More"}
//   </button>
// )}


// That means:

// The backend (get-acct-pay-trans) must return meta.hasMore: true when there are more than PAGE_SIZE (6) results in total.

// If your seed/test data only created 3 records (like earlier), then hasMore = false → button hidden.

// 👉 To test, just simulate >6 transactions for a user in your seeder. Then the API will report hasMore: true and the button will appear.

// ✅ Summary:

// Dropdown only shows 1 week because only 1 week of data exists in the DB right now. More weeks = more dropdown options automatically.

// “View More” is hidden because your API is telling the frontend there aren’t more than 6 records. With >6, it will appear.

// Do you want me to adjust the seeder script so it generates transactions across multiple weeks (so you can immediately test the dropdown)?



/// test run
// ChatGPT said:

// Perfect 👍 we’ll tweak your simulate-referrals-via-service.js so it can create fake signups spread across multiple weeks instead of just one day. That way:

// Your AccountsReceivables dropdown will show multiple weeks.

// You can also test the “View More” button by creating >6 total transactions.

// 🔧 How to update your seeder

// In simulate-referrals-via-service.js (or your test seeder), add a --weeks <n> option. That will let you spread signups across multiple prior weeks.

// // add this near top in parseArgs docs
// //   --weeks <n>   (default 1) number of weeks back to simulate

// const args = parseArgs(process.argv.slice(2));
// const numWeeks = parseInt(args.weeks || "1", 10);


// Then, instead of always seeding for args.date, loop backwards by weeks:

// for (let w = 0; w < numWeeks; w++) {
//   // each week: subtract 7 * w days from args.date
//   const d = new Date(args.date);
//   d.setDate(d.getDate() - w * 7);
//   const weekDateStr = d.toISOString().slice(0, 10); // YYYY-MM-DD

//   console.log(`\n[simulate] Week ${w + 1} → ${weekDateStr}`);

//   // reuse your existing slot/plan loop
//   const slots = enumerateSlots(startTime, endTime);
//   for (let i = 0; i < plan.length && i < slots.length; i++) {
//     const slot = slots[i];
//     const n = plan[i] || 0;
//     if (n <= 0) continue;

//     const slotStartUtc = await findUtcInsideSlot(weekDateStr, slot, timeZone);
//     const stepMs = Math.floor(3_600_000 / (n + 1));

//     for (let j = 0; j < n; j++) {
//       const t = new Date(slotStartUtc.getTime() + (j + 1) * stepMs);
//       const iso = t.toISOString();
//       await authService.countAmbassadorReferral(args.user, iso);
//     }
//   }
// }

// ✅ Example command

// To simulate 2 weeks of data, with 10 signups in the first slot each week:

// yarn simulate:referrals \
//   --user Lowlow123@ \
//   --date 2025-08-13 \
//   --start 16:00:00 --end 23:00:00 \
//   --tz America/New_York \
//   --plan 10 \
//   --weeks 2 \
//   --createUser --reset --verbose


// That will:

// Create records for week of Aug 13 and week of Aug 6.

// Drop >6 records if you want “View More” to appear.

// Show multiple weeks in your dropdown.

// ⚡ Question: do you want me to also make the seeder auto-distribute signups across different slots (instead of one slot per week), so you can see fuller lists? Or just keep it simple, all in one slot per week?